import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { PathwayNav } from '../components/pathway/PathwayNav';
import { StartPanel } from '../components/pathway/StartPanel';
import { ResultsPanel } from '../components/pathway/ResultsPanel';
import { PathwayTray } from '../components/pathway/PathwayTray';
import { ShareModal } from '../components/pathway/ShareModal';
import { PathwayView, PathwayViewToggle } from '../components/pathway/PathwayViewToggle';
import { SavedPathwaysView } from '../components/pathway/saved/SavedPathwaysView';
import { ProvenPathways } from '../components/pathway/ProvenPathways';
import { EditIntentModal } from '../components/pathway/saved/EditIntentModal';
import { NamePathwayModal, NamePathwayRequest } from '../components/pathway/NamePathwayModal';
import { ConfirmModal } from '../components/pathway/ConfirmModal';
import { cohorts, pathwayTemplates } from '../data/pathwayTemplates';
import { savedPathways as initialSavedPathways } from '../data/savedPathways';
import { moduleById } from '../data/skillModules';
import { AudienceLevel, EditMode, PathwayTemplate, SavedPathway } from '../types/pathway';
import { ALL_TERMS, matchModules, parseTopics, readConversation, relatedTerms } from '../utils/pathwayMatching';
type EntryMode = 'notes' | 'topics';
interface OpenSnapshot {
  pathway: SavedPathway;
  items: string[];
  name: string;
  company: string;
  cohortId: string;
  audience: AudienceLevel | 'any';
  terms: string[];
}
export function PathwayBuilder() {
  const [view, setView] = useState<PathwayView>('build');
  const [saved, setSaved] = useState<SavedPathway[]>(initialSavedPathways);
  /** Pending "name it first" step for a duplicate or a template. */
  const [naming, setNaming] = useState<{
    kind: 'duplicate';
    pathway: SavedPathway;
    request: NamePathwayRequest;
  } | {
    kind: 'template';
    template: PathwayTemplate;
    request: NamePathwayRequest;
  } | null>(null);
  const [mode, setMode] = useState<EntryMode>('topics');
  const [notes, setNotes] = useState('');
  const [topics, setTopics] = useState('');
  // Null means "follow whatever the notes say"; any other value is the rep's
  // explicit choice, which always wins.
  const [audienceOverride, setAudienceOverride] = useState<AudienceLevel | 'any' | null>(null);
  // Matching runs live off the notes/topics, debounced so a paragraph being
  // typed doesn't yank the list around on every keystroke.
  const [liveTerms, setLiveTerms] = useState<string[]>([]);
  const [browseAll, setBrowseAll] = useState(false);
  const [addedTerms, setAddedTerms] = useState<string[]>([]);
  const [removedTerms, setRemovedTerms] = useState<string[]>([]);
  const [items, setItems] = useState<string[]>([]);
  const [pathwayName, setPathwayName] = useState('');
  // Null means "follow whatever the notes say"; anything else is the rep's own
  // value, which always wins.
  const [companyOverride, setCompanyOverride] = useState<string | null>(null);
  const [cohortId, setCohortId] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  // Saved-pathway editing. The snapshot is what was loaded, so Revert and
  // Cancel can restore it exactly.
  const [snapshot, setSnapshot] = useState<OpenSnapshot | null>(null);
  const [editMode, setEditMode] = useState<EditMode>('none');
  const [intentFor, setIntentFor] = useState<SavedPathway | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const liveRead = useMemo(() => readConversation(notes), [notes]);
  const company = companyOverride ?? liveRead.company ?? '';
  const companyFromNotes = companyOverride === null && (liveRead.company ?? '').length > 0;
  const setCompany = (value: string) => setCompanyOverride(value);
  const audience: AudienceLevel | 'any' = audienceOverride ?? liveRead.level ?? 'any';
  const setAudience = (value: AudienceLevel | 'any') => setAudienceOverride(value);
  const activeTerms = useMemo(() => Array.from(new Set([...liveRead.strong, ...addedTerms])).filter(term => !removedTerms.includes(term)), [liveRead.strong, addedTerms, removedTerms]);
  const suggestedTerms = useMemo(() => liveRead.weak.filter(term => !activeTerms.includes(term)), [liveRead.weak, activeTerms]);
  const toggleTerm = (term: string) => {
    if (activeTerms.includes(term)) {
      setAddedTerms(prev => prev.filter(t => t !== term));
      setRemovedTerms(prev => prev.includes(term) ? prev : [...prev, term]);
      return;
    }
    setRemovedTerms(prev => prev.filter(t => t !== term));
    setAddedTerms(prev => prev.includes(term) ? prev : [...prev, term]);
  };
  const handleNotesChange = (value: string) => {
    setNotes(value);
    if (value.trim().length === 0) {
      setAddedTerms([]);
      setRemovedTerms([]);
      setAudienceOverride(null);
      setCompanyOverride(null);
    }
  };
  const typedTerms = useMemo(() => mode === 'notes' ? activeTerms : parseTopics(topics), [mode, activeTerms, topics]);
  const typedKey = typedTerms.join('|');
  useEffect(() => {
    const timer = window.setTimeout(() => setLiveTerms(typedKey.length ? typedKey.split('|') : []), 350);
    return () => window.clearTimeout(timer);
  }, [typedKey]);
  const terms = browseAll ? [] : liveTerms;
  const hasResults = browseAll || liveTerms.length > 0;
  const results = useMemo(() => matchModules(terms, audience === 'any' ? null : audience), [terms, audience]);
  const suggestions = useMemo(() => relatedTerms(terms, results), [terms, results]);
  const selectedCohort = cohorts.find(c => c.id === cohortId) ?? null;
  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3200);
  };

  /** Seeds the topic field, used when a template or saved pathway is loaded. */
  const seedTopics = (nextTerms: string[]) => {
    setMode('topics');
    setTopics(nextTerms.join(', '));
    setLiveTerms(nextTerms);
    setBrowseAll(false);
  };
  const startOver = () => {
    setNotes('');
    setTopics('');
    setAddedTerms([]);
    setRemovedTerms([]);
    setAudienceOverride(null);
    setCompanyOverride(null);
    setLiveTerms([]);
    setBrowseAll(false);
    setMode('topics');
  };

  /**
   * Any change to an opened saved pathway needs an explicit decision first, so
   * a rep never silently overwrites something a prospect has already seen.
   * Returns true when the edit should be held back pending that decision.
   */
  const needsIntent = () => {
    if (!snapshot || editMode !== 'none') return false;
    setIntentFor(snapshot.pathway);
    return true;
  };
  const toggleModule = (moduleId: string) => {
    if (needsIntent()) return;
    setItems(prev => prev.includes(moduleId) ? prev.filter(id => id !== moduleId) : [...prev, moduleId]);
  };
  const moveItem = (moduleId: string, direction: -1 | 1) => {
    if (needsIntent()) return;
    setItems(prev => {
      const position = prev.indexOf(moduleId);
      const target = position + direction;
      if (position === -1 || target < 0 || target >= prev.length) return prev;
      const next = prev.slice();
      next[position] = next[target];
      next[target] = moduleId;
      return next;
    });
  };
  const handleNameChange = (value: string) => {
    if (needsIntent()) return;
    setPathwayName(value);
  };
  const handleCompanyChange = (value: string) => {
    if (needsIntent()) return;
    setCompany(value);
  };
  const handleClear = () => {
    if (needsIntent()) return;
    setConfirmClearOpen(true);
  };
  const confirmClear = () => {
    setItems([]);
    setConfirmClearOpen(false);
    showToast('Cleared the modules from your pathway');
  };
  const loadSaved = (pathway: SavedPathway) => {
    const nextItems = [...pathway.moduleIds];
    const nextCohort = pathway.cohortId ?? '';
    const derived = Array.from(new Set(pathway.moduleIds.flatMap(id => moduleById(id)?.keywords.slice(0, 2) ?? []))).slice(0, 3);
    setItems(nextItems);
    setPathwayName(pathway.name);
    setCompany(pathway.organization);
    setAudience(pathway.audience);
    setCohortId(nextCohort);
    setSnapshot({
      pathway,
      items: nextItems,
      name: pathway.name,
      company: pathway.organization,
      cohortId: nextCohort,
      audience: pathway.audience,
      terms: derived
    });
    setEditMode('none');
    setView('build');
    seedTopics(derived);
  };
  const startDuplicate = (pathway: SavedPathway) => {
    setNaming({
      kind: 'duplicate',
      pathway,
      request: {
        title: 'Duplicate pathway',
        description: <>
            Copies all {pathway.moduleIds.length} modules from{' '}
            <span className="font-medium text-gray-900" data-id="element-2568" data-mp="3gero115cwdmn">{pathway.name}</span> in
            the same order. The original is untouched.
          </>,
        suggestedName: `${pathway.name} (copy)`,
        confirmLabel: 'Duplicate Pathway'
      }
    });
  };
  const startTemplate = (template: PathwayTemplate) => {
    setNaming({
      kind: 'template',
      template,
      request: {
        title: 'Name your pathway',
        description: <>
            Starts from{' '}
            <span className="font-medium text-gray-900" data-id="element-2569" data-mp="m0ont97qbbbl">{template.name}</span>{' '}
            with all {template.moduleIds.length} modules. Give it a name your
            prospect will recognize — it appears on the PDF.
          </>,
        suggestedName: template.name,
        confirmLabel: 'Start Building'
      }
    });
  };
  const confirmName = (name: string) => {
    if (!naming) return;
    if (naming.kind === 'duplicate') {
      // Duplicating stays on the list, so the rep can copy several in a row.
      const copy: SavedPathway = {
        ...naming.pathway,
        id: `${naming.pathway.id}-copy-${Date.now()}`,
        name,
        cohortId: null,
        updatedAt: new Date().toISOString().slice(0, 10),
        updatedLabel: 'Updated just now'
      };
      setSaved(prev => [copy, ...prev]);
      setNaming(null);
      showToast(`Created “${name}”`);
      return;
    }
    const {
      template
    } = naming;
    setItems([...template.moduleIds]);
    setPathwayName(name);
    setCompanyOverride(null);
    setAudience(template.audience);
    setCohortId('');
    setSnapshot(null);
    setEditMode('none');
    setNaming(null);
    setView('build');
    seedTopics(template.terms);
    showToast(`Started “${name}” from ${template.name}`);
  };
  const restoreSnapshot = () => {
    if (!snapshot) return;
    setItems(snapshot.items);
    setPathwayName(snapshot.name);
    setCompany(snapshot.company);
    setCohortId(snapshot.cohortId);
    setAudience(snapshot.audience);
  };
  const chooseIntent = (next: EditMode) => {
    if (!snapshot) return;
    setEditMode(next);
    setIntentFor(null);
    showToast(`Changes will save to “${snapshot.name}”`);
  };
  const cancelIntent = () => {
    restoreSnapshot();
    setIntentFor(null);
  };
  const revertEdits = () => {
    restoreSnapshot();
    setEditMode('none');
    showToast('Reverted to the saved pathway');
  };
  const editContext = useMemo(() => {
    if (!snapshot || editMode === 'none') return null;
    return `Editing ${snapshot.name}`;
  }, [snapshot, editMode]);
  const handleOpen = (pathway: SavedPathway) => {
    if (items.length > 0 && editMode !== 'none') {
      const ok = window.confirm('Editing this pathway replaces the one you have in progress. Continue?');
      if (!ok) return;
    }
    loadSaved(pathway);
  };
  const buildHasWork = items.length > 0;

  /** A chip edit reaches whichever input produced it. */
  const toggleSearchTerm = (term: string) => {
    if (mode === 'notes') {
      toggleTerm(term);
      return;
    }
    const parsed = parseTopics(topics);
    const next = parsed.includes(term) ? parsed.filter(t => t !== term) : [...parsed, term];
    setTopics(next.join(', '));
  };

  // Weak detections first, then terms the results themselves suggest.
  const panelSuggestions = useMemo(() => {
    const base = mode === 'notes' ? suggestedTerms : [];
    return Array.from(new Set([...base, ...suggestions])).filter(term => !typedTerms.includes(term)).slice(0, 6);
  }, [mode, suggestedTerms, suggestions, typedTerms]);
  const trayProps = {
    name: pathwayName,
    company,
    companyFromNotes,
    items,
    audience,
    cohortId,
    cohorts,
    editContext,
    onRevert: revertEdits,
    onNameChange: handleNameChange,
    onCompanyChange: handleCompanyChange,
    onAudienceChange: setAudience,
    onCohortChange: (nextCohortId: string) => {
      setCohortId(nextCohortId);
      // A cohort already knows its organization — save the rep retyping it.
      const cohort = cohorts.find(c => c.id === nextCohortId);
      if (cohort && !company.trim()) setCompany(cohort.organization);
    },
    onRemove: toggleModule,
    onMove: moveItem,
    onClear: handleClear,
    onShare: () => setShareOpen(true)
  };
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-2570" data-mp="5aw9mbp37h1o">
      <PathwayNav data-id="element-2571" data-mp="a8pijchryp5n" />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2572" data-mp="a7wh8r1l4ruw">
        <header className="mb-6 max-w-3xl" data-id="element-2573" data-mp="xyaaulztiyrl">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#30718D] mb-1" data-id="element-2574" data-mp="xzlf0mx6e8j8">
            Skills Center
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-2575" data-mp="11xrf5c3idpv6">
            Pathway Builder
          </h1>
          <p className="text-sm text-gray-600" data-id="element-2576" data-mp="o2gtmpb5mh3h">
            Turn a prospect conversation into a sequenced learning pathway you
            can send the same day.
          </p>
        </header>

        <div className="mb-6" data-id="element-2577" data-mp="38soa6ehah9bh">
          <PathwayViewToggle view={view} buildHasWork={buildHasWork} onChange={setView} data-id="element-2578" data-mp="3dpxw3k5gz2uk" />
        </div>

        {view === 'saved' ? <>
            <SavedPathwaysView pathways={saved} onOpen={handleOpen} onDuplicate={startDuplicate} onBuildNew={() => setView('build')} data-id="element-2579" data-mp="3o7tbxp622w45" />
            <ProvenPathways templates={pathwayTemplates} onUseTemplate={startTemplate} data-id="element-2580" data-mp="3f86ibpjkiuln" />
          </> : <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start" data-id="element-2581" data-mp="fqfa8bbave1w">
            <div className="space-y-6" data-id="element-2582" data-mp="brpnhs4dgk5l">
              <StartPanel mode={mode} notes={notes} topics={topics} audience={audience} liveRead={liveRead} activeTerms={typedTerms} suggestedTerms={panelSuggestions} vocabulary={ALL_TERMS} detectedLevel={liveRead.level} matchCount={hasResults ? results.length : null} hasSearch={notes.trim().length > 0 || topics.trim().length > 0 || audience !== 'any' || browseAll} onReset={startOver} onToggleTerm={toggleSearchTerm} onModeChange={setMode} onNotesChange={handleNotesChange} onClearNotes={() => handleNotesChange('')} onTopicsChange={setTopics} onAudienceChange={setAudience} onBrowseAll={() => setBrowseAll(true)} data-id="element-2583" data-mp="6rl7e37dlrfw" />

              <AnimatePresence initial={false} data-id="element-2584" data-mp="354rjdh9scu7h">
                {hasResults && <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-2585" data-mp="3a32r9mgisun2">
                    <ResultsPanel terms={terms} results={results} pathwayIds={items} audience={audience} onToggleModule={toggleModule} data-id="element-2586" data-mp="17o3xp6s04mj2" />
                  </motion.div>}
              </AnimatePresence>
            </div>

            <PathwayTray {...trayProps} data-id="element-2587" data-mp="1cllz8c3ovzh1" />
          </div>}
      </div>

      <EditIntentModal pathway={intentFor} onUpdate={() => chooseIntent('updating')} onCancel={cancelIntent} data-id="element-2588" data-mp="1qlwqvhctpi39" />

      <ConfirmModal open={confirmClearOpen} title="Clear this pathway?" description={<>
            This removes all {items.length}{' '}
            {items.length === 1 ? 'module' : 'modules'} from{' '}
            <span className="font-medium text-gray-900" data-id="element-2590" data-mp="29co792v8a8r5">
              {pathwayName.trim() || 'your pathway'}
            </span>
            . The company and pathway name stay as they are.
          </>} confirmLabel="Clear Modules" onConfirm={confirmClear} onCancel={() => setConfirmClearOpen(false)} data-id="element-2589" data-mp="1llsfu6uopfkf" />

      <NamePathwayModal request={naming?.request ?? null} onConfirm={confirmName} onCancel={() => setNaming(null)} data-id="element-2591" data-mp="2syg8oga5yc1a" />

      <ShareModal open={shareOpen} pathwayName={pathwayName} company={company} items={items} audience={audience} cohort={selectedCohort} onCompanyChange={setCompany} onClose={() => setShareOpen(false)} data-id="element-2592" data-mp="2jy1ltchjwovo" />

      <AnimatePresence data-id="element-2593" data-mp="2a27yqz67hczn">
        {toast && <motion.div role="status" aria-live="polite" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 12
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2" data-id="element-2594" data-mp="1wa071lts4c7t">
            <div className="flex items-center gap-2 rounded-lg border border-[#05634B]/20 bg-white px-4 py-3 shadow-lg" data-id="element-2595" data-mp="26rwyfi3n2z9s">
              <CheckCircle2 size={16} className="text-[#007558]" data-id="element-2596" data-mp="21skfh8fw2cch" />
              <p className="text-sm font-medium text-gray-900" data-id="element-2597" data-mp="21qzhf8ud8b9v">{toast}</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}